/**
 * Navigation Grouping Utilities
 *
 * Functions for organizing and categorizing navigation items
 * for better mobile display and user experience.
 */

import { NavigationItem } from "../types";
import { NavigationGroup, GroupedNavigationItems } from "../mobile-drawer-types";

/**
 * Group navigation items by category for better organization (mobile-specific grouping)
 */
export function groupNavigationItems(items: NavigationItem[]): GroupedNavigationItems {
  // Define categories and their priorities for mobile
  const categoryOrder = ["Dashboard", "CRM", "Work", "Reports", "Admin", "Other", "Settings"];
  const groups: { [key: string]: NavigationItem[] } = {};
  const ungrouped: NavigationItem[] = [];

  items.forEach((item) => {
    // Try to categorize items based on their name or path
    let category = "";

    if (item.name.toLowerCase().includes("dashboard") || item.href.includes("dashboard")) {
      category = "Dashboard";
    } else if (
      item.name.toLowerCase().includes("crm") ||
      item.href.includes("crm") ||
      item.name.toLowerCase().includes("client") ||
      item.name.toLowerCase().includes("contact") ||
      item.name.toLowerCase().includes("contract") ||
      item.name.toLowerCase().includes("companies")
    ) {
      category = "CRM";
    } else if (
      item.name.toLowerCase().includes("ticket") ||
      item.href.includes("ticket") ||
      item.name.toLowerCase().includes("task") ||
      item.href.includes("task") ||
      item.name.toLowerCase().includes("time") ||
      item.href.includes("time") ||
      item.name.toLowerCase().includes("unified") ||
      item.href.includes("unified") ||
      item.name.toLowerCase().includes("work") ||
      item.href.includes("work")
    ) {
      // Group tickets, tasks, and time tracking under "Work" on mobile
      category = "Work";
    } else if (item.name.toLowerCase().includes("report") || item.href.includes("report")) {
      category = "Reports";
    } else if (item.name.toLowerCase().includes("admin") || item.href.includes("admin")) {
      category = "Admin";
    } else if (
      item.name.toLowerCase().includes("chat") ||
      item.href.includes("chat") ||
      item.name.toLowerCase().includes("ai") ||
      item.href.includes("ai")
    ) {
      category = "Other";
    } else if (
      item.name.toLowerCase().includes("settings") ||
      item.name.toLowerCase().includes("account")
    ) {
      category = "Settings";
    } else {
      ungrouped.push(item);
      return;
    }

    if (!groups[category]) {
      groups[category] = [];
    }
    groups[category].push(item);
  });

  // Return groups in order, only including categories that have items
  const orderedGroups: NavigationGroup[] = categoryOrder
    .filter((category) => groups[category] && groups[category].length > 0)
    .map((category) => ({ category, items: groups[category] }));

  return { orderedGroups, ungrouped };
}
